import { ShopigurusHTMLElement } from '@shopigurus/base';
import flowState from '@shopigurus/flow-state';

/**
 * ShpfltWelcomePopup — Homepage entry modal with three options:
 * Recogida / Envío / Ver menú.
 *
 * - 'pickup' or 'shipping' → sets flow mode + opens Zapiet Eats modal (delivery tab pre-selected)
 * - 'browse'              → sets flow mode + closes; user explores without commitment
 *
 * @example
 * <shpflt-welcome-popup data-section-id="{{ section.id }}">
 *   <dialog ref="dialog">…</dialog>
 * </shpflt-welcome-popup>
 */
class ShpfltWelcomePopup extends ShopigurusHTMLElement {
  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLDialogElement} */
    const dialog = this.refs.dialog;
    if (!dialog) return;

    // Bind option clicks
    for (const btn of this.querySelectorAll('[data-mode]')) {
      btn.addEventListener(
        'click',
        (e) => this.#handleOptionClick(e),
        { signal: this.abortController.signal }
      );
    }

    // Backdrop click closes the dialog without setting a mode.
    // (User dismissed without choosing — treat as "browse" so we don't pester them.)
    dialog.addEventListener(
      'click',
      (e) => {
        if (e.target === dialog) {
          flowState.setMode('browse');
          dialog.close();
        }
      },
      { signal: this.abortController.signal }
    );

    // ESC also dismisses → treat as browse
    dialog.addEventListener(
      'cancel',
      () => {
        flowState.setMode('browse');
      },
      { signal: this.abortController.signal }
    );

    // Initial open decision:
    //  - Theme editor: respect data-show-in-editor
    //  - Live site: only if no mode chosen yet AND we're on the homepage
    queueMicrotask(() => this.#maybeOpen());
  }

  onSectionSelect() {
    // In editor, re-open as modal if it was closed while editing.
    const dialog = this.refs.dialog;
    if (dialog && !dialog.open && this.dataset.showInEditor === 'true') {
      this.#openSafely(dialog);
    }
  }

  #maybeOpen() {
    /** @type {HTMLDialogElement} */
    const dialog = this.refs.dialog;
    if (!dialog) return;

    const inEditor = window.Shopify?.designMode === true;
    if (inEditor) {
      // ALWAYS modal in editor too — otherwise dialog.show() renders the dialog
      // inline at its DOM position (which is after the footer), making it look
      // like a footer block. Modal promotes it to top layer (centered overlay).
      if (this.dataset.showInEditor === 'true') this.#openSafely(dialog);
      return;
    }

    // Live site: only on home and only if no mode yet
    const isHome = location.pathname === '/' || location.pathname === '';
    if (!isHome) return;
    if (flowState.getMode() !== null) return;

    this.#openSafely(dialog);
  }

  /**
   * Open the dialog as modal (top-layer overlay). Falls back to inline open
   * only if showModal() is unsupported, applying a CSS class so the fallback
   * styles position it as a fixed centered overlay instead of inline.
   * @param {HTMLDialogElement} dialog
   */
  #openSafely(dialog) {
    try {
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
        return;
      }
    } catch { /* already open or detached — try fallback */ }
    // Fallback for very old browsers (Safari < 15.4):
    dialog.setAttribute('open', '');
    dialog.classList.add('shpflt-welcome-popup__dialog--fallback');
  }

  /** @param {MouseEvent} e */
  #handleOptionClick(e) {
    const btn = e.currentTarget;
    const mode = btn.dataset.mode;
    if (!mode) return;

    // Visual feedback: mark the clicked option active (in case user reopens dialog)
    for (const b of this.querySelectorAll('[data-mode]')) {
      b.classList.toggle('shpflt-welcome-popup__option--active', b === btn);
    }

    flowState.setMode(mode);

    /** @type {HTMLDialogElement} */
    const dialog = this.refs.dialog;
    dialog?.close();

    if (mode === 'pickup' || mode === 'shipping') {
      flowState.requestZapiet(mode);
      return;
    }

    // 'browse' → redirect to the configured menu URL if present.
    // No URL configured: just close, user keeps navigating from where they are.
    // Redirect works the same in the theme editor as on the live site, so the
    // merchant can test the link from Customize without having to leave it.
    if (mode === 'browse') {
      const browseUrl = this.dataset.browseUrl;
      if (browseUrl) {
        window.location.href = browseUrl;
      }
    }
  }
}

customElements.define('shpflt-welcome-popup', ShpfltWelcomePopup);
